import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@mui/material";
import { postVisual } from "../api";
import { useToast } from "../../../components/Toast";

interface FeltSenseToolProps {
  patientId: string;
  sessionId?: string;
  getToken: () => Promise<string | null>;
}

interface Attempt { prompt: string; url: string; }

/* Focusing (Gendlin): iterate a symbolic image toward the felt sense.
   Each refinement is appended to the running description and re-rendered;
   earlier attempts stay visible so the client can say "more like the second one". */
export function FeltSenseTool({ patientId, sessionId, getToken }: FeltSenseToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [base, setBase] = useState("");
  const [refinement, setRefinement] = useState("");
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [busy, setBusy] = useState(false);

  const generate = async (prompt: string) => {
    try {
      setBusy(true);
      const res = await postVisual(getToken, {
        patient_id: patientId, session_id: sessionId, kind: "single", prompt,
      });
      setAttempts((prev) => [...prev, { prompt, url: res.images[0].url }]);
      setRefinement("");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : t("experiential.genError"));
    } finally {
      setBusy(false);
    }
  };

  const current = attempts.length > 0 ? attempts[attempts.length - 1] : null;

  return (
    <div>
      <TextField
        label={t("experiential.feltSenseDesc")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={base}
        onChange={(e) => setBase(e.target.value)}
        disabled={attempts.length > 0}
        sx={{ mb: 2 }}
      />

      {attempts.length === 0 ? (
        <Button variant="contained" size="small" onClick={() => generate(base)} disabled={busy || !base.trim()}>
          {t("experiential.generateImages")}
        </Button>
      ) : (
        <>
          <img
            src={current!.url}
            alt={t("experiential.feltSenseDesc")}
            style={{ width: "100%", borderRadius: 6, marginBottom: 12 }}
          />
          <TextField
            label={t("experiential.refineLabel")}
            helperText={t("experiential.refineHint")}
            fullWidth
            size="small"
            value={refinement}
            onChange={(e) => setRefinement(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => generate(`${current!.prompt}\nAdjust: ${refinement.trim()}`)}
            disabled={busy || !refinement.trim()}
            sx={{ mb: 2 }}
          >
            {t("experiential.refine")}
          </Button>

          {attempts.length > 1 && (
            <div className="exp-felt-history">
              {attempts.slice(0, -1).map((a, i) => (
                <img key={a.url} src={a.url} alt={`${t("experiential.attempt")} ${i + 1}`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
